/**
 * Solanaland
 *
 * Author: Venus Lumanglas
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Table, Card } from "core/components";
import formatter from "core/formatter";

const ChangeOrderItems = ({ changeOrder, reset }): JSX.Element => {
  const changeOrderTaskItems = changeOrder.change_order_task_items;
  const deductedQuantityColumns = changeOrder.change_order_type == "add" ? ["deducted_quantity"] : [];

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        Header: "Task",
        accessor: "task",
        Cell: (props) => {
          const task = props.value;

          return (
            <>
              <div className="text-bold text-truncate" title={task.task_classification.name} data-bs-toggle="tooltip">
                {`${task.code} - ${task.task_classification.name}`}
              </div>
              <p style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }} title={task.name} data-bs-toggle="tooltip">
                {task.name}
              </p>
            </>
          );
        }
      },
      {
        Header: "Item",
        accessor: "item",
        Cell: (props) => {
          const item = props.value;

          return (
            <>
              <div className="text-bold" title={item.code} data-bs-toggle="tooltip">
                {item.code}
              </div>
              <p style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }} title={item.name} data-bs-toggle="tooltip">
                {item.name}
              </p>
            </>
          );
        }
      },
      {
        Header: "Budgeted Quantity",
        accessor: "budgeted_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{`${props.value} ${props.row.original.unit_measure_code}`}</p>
            </div>
          );
        }
      },
      {
        id: "deducted_quantity",
        Header: "Deducted Quantity",
        accessor: "deducted_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{`${props.value} ${props.row.original.unit_measure_code}`}</p>
            </div>
          );
        }
      },
      {
        Header: "Budgeted Unit Rate",
        accessor: "budgeted_unit_rate",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-right">{formatter.format(props.value)}</p>
            </div>
          );
        }
      },
      {
        Header: "Budgeted Amount",
        accessor: "budgeted_amount",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-right">{formatter.format(props.value)}</p>
            </div>
          );
        }
      },
      {
        Header: "Actual Issued Quantity",
        accessor: "actual_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center text-bold">{props.value || "-"}</p>
            </div>
          );
        }
      },
      {
        Header: "Actual Issued Amount",
        accessor: "total_actual_cost",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-right text-bold">{formatter.format(props.value || 0.0)}</p>
            </div>
          );
        }
      },
      {
        Header: "Remaining Quantity",
        accessor: "remaining_quantity",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Variance",
        accessor: "cost_variance",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              {props.value < 0 ? (
                <p className="text-truncate text-right" style={{ color: "red" }}>{`(${Math.abs(parseFloat(props.value))?.toLocaleString("en-US", {
                  minimumFractionDigits: 2
                })})`}</p>
              ) : (
                <p className="text-truncate text-right text-bold">
                  {props.value ? parseFloat(props.value).toLocaleString("en-US", { minimumFractionDigits: 2 }) : "-"}
                </p>
              )}
            </div>
          );
        }
      }
    ],
    []
  );

  React.useEffect(() => {
    reset({
      id: changeOrder.id,
      include_budget: changeOrder.include_budget
    });
  }, []);

  return (
    <React.StrictMode>
      <Container fluid={true}>
        <Row>
          <Col>
            <Card.Body>
              <Table columns={columns} data={changeOrderTaskItems} hiddenColumns={[...deductedQuantityColumns]} />
            </Card.Body>
          </Col>
        </Row>
      </Container>
    </React.StrictMode>
  );
};

export default ChangeOrderItems;
